import { Button, Flex, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { useGetDailyConcentrationInsightsQuery } from '../../requests/requests.gql.generated';
import {
  getConcentrationLimitFormatsByKey,
  ConcentrationLimitFormatMap,
} from '../shared/portfolioChartUtils';
import { PortfolioCoreBorrowingBaseChartTemplate } from '../shared/PortfolioCoreBorrowingBaseChartTemplate';
import {
  chartGroupMap,
  generateConcentrationLimitChartConfig,
} from './portfolioBorrowingBaseChartConfigs';
import { PortfolioEligibilityKickoutsChart } from './PortfolioBorrowingBaseEligibilityChart';
import { PortfolioConcentrationLimitsChartTemplate } from './PortfolioConcentrationLimitsChartTemplate';
import { PortfolioExcessConcentrationChart } from './PortfolioExcessConcentrationChart';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { useQueryParams } from '~/packages/common-frontend/hooks/useQueryParams';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { creditFacilityIdQueryParam } from '~/packages/credit-facility/ui/CreditFacilitySelect';
import { useGetCurrentCreditAgreementFullArtifactQuery } from '~/packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const borrowingBaseDisplayModes = [
  'all',
  'borrowingBase',
  'eligibility',
  'concentration',
] as const;
type BorrowingBaseChartDisplayMode = typeof borrowingBaseDisplayModes[number];
const isBorrowingBaseChartDisplayMode = (
  test: any,
): test is BorrowingBaseChartDisplayMode => {
  return (
    test &&
    typeof test === 'string' &&
    _.includes(borrowingBaseDisplayModes, test)
  );
};

const borrowingBaseDisplayModeQueryParam = 'display';

const { borrowingBase, eligibility } = chartGroupMap;

type PortfolioBorrowingBaseContainerProps = { creditFacilityId: string };
export const PortfolioBorrowingBaseContainer = ({
  creditFacilityId,
}: PortfolioBorrowingBaseContainerProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const query = useQueryParams();
  const queryDisplayMode = query.get(borrowingBaseDisplayModeQueryParam);
  const displayMode = isBorrowingBaseChartDisplayMode(queryDisplayMode)
    ? queryDisplayMode
    : 'all';

  const {
    data: concentrationData,
    loading: isConcentrationDataLoading,
  } = useGetDailyConcentrationInsightsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
  });

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
  });

  const concentrationLimitFormatMap = useMemo<ConcentrationLimitFormatMap>(() => {
    if (!concentrationData) {
      return new Map();
    }
    return getConcentrationLimitFormatsByKey(
      concentrationData.getDailyConcentrationInsights,
    );
  }, [concentrationData]);

  const creditAgreementConcentrationLimits: CreditAgreement.DomainEntity.ConcentrationLimitProperty[] = useMemo(() => {
    if (!creditAgreementData?.currentCreditAgreementFullArtifact) {
      return [];
    }

    const creditAgreementString =
      creditAgreementData.currentCreditAgreementFullArtifact.creditAgreement;
    const creditAgreementId =
      creditAgreementData.currentCreditAgreementFullArtifact.id;

    let parsedCreditAgreement = null;
    if (creditAgreementString) {
      try {
        parsedCreditAgreement = parseCreditAgreementFullArtifact(
          creditAgreementString,
        );
      } catch (err) {
        Sentry.captureException(err);

        toast({
          ...errorToastConfig,
          id: 'errorGetCurrentCreditAgreementFullArtifact',
          title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
        });
      }
    }

    finleyAssert(
      parsedCreditAgreement,
      `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
    );

    const singleLoanProductConcentrationLimits = parsedCreditAgreement.borrowingBaseMechanics.calcAgentMechanics.loanProductMechanics.flatMap(
      (loanProductMechanic) => loanProductMechanic.concentrationLimits,
    );
    const multiLoanProductConcentrationLimits =
      parsedCreditAgreement.borrowingBaseMechanics.calcAgentMechanics
        .multiLoanProductConcentrationLimits;

    return [
      ...singleLoanProductConcentrationLimits,
      ...multiLoanProductConcentrationLimits,
    ];
  }, [creditAgreementData, creditFacilityId, toast]);

  // @dani portfolio update this skeleton design
  if (
    !concentrationData ||
    isConcentrationDataLoading ||
    !creditAgreementData ||
    isCreditAgreementLoading
  ) {
    return <Skeleton h="700px" />;
  }

  const totalChartCounts = {
    borrowingBase: borrowingBase.length,
    eligibility: eligibility.length + 1, // 1 additional for the aggregate chart
    concentration: concentrationLimitFormatMap.size + 1, // 1 additional for the aggregate chart
  };

  const onSetDisplayMode = (
    selectedDisplayMode: BorrowingBaseChartDisplayMode,
  ) => {
    const covenantsBaseRoute = '/portfolio/borrowing-base';
    const queryParams = `?${creditFacilityIdQueryParam}=${creditFacilityId}&${borrowingBaseDisplayModeQueryParam}=${selectedDisplayMode}`;

    navigate(`${covenantsBaseRoute}${queryParams}`);
  };

  return (
    <Flex direction="column" gap="2.5rem">
      <Flex gap="0.5rem">
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'all'}
          onClick={() => onSetDisplayMode('all')}
        >
          Show All {_.sum(Object.values(totalChartCounts))}
        </Button>
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'borrowingBase'}
          onClick={() => onSetDisplayMode('borrowingBase')}
        >
          Borrowing Base {totalChartCounts.borrowingBase}
        </Button>
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'eligibility'}
          onClick={() => onSetDisplayMode('eligibility')}
        >
          Eligibility {totalChartCounts.eligibility}
        </Button>
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'concentration'}
          onClick={() => onSetDisplayMode('concentration')}
        >
          Concentration {totalChartCounts.concentration}
        </Button>
      </Flex>
      {_.map(chartGroupMap, (chartMaps, group) =>
        _.map(chartMaps, (chart) => {
          return (
            (displayMode === 'all' || displayMode === group) && (
              <PortfolioCoreBorrowingBaseChartTemplate
                creditFacilityId={creditFacilityId}
                chartTitle={chart.chartTitle}
                chartConfig={chart.config}
                key={`portfolio-borrowing-base-chart-${chart.chartTitle}`}
              />
            )
          );
        }),
      )}
      {(displayMode === 'all' || displayMode === 'eligibility') && (
        <PortfolioEligibilityKickoutsChart
          creditFacilityId={creditFacilityId}
          chartTitle="Eligibility Kickouts"
        />
      )}

      {(displayMode === 'all' || displayMode === 'concentration') && (
        <PortfolioExcessConcentrationChart
          creditFacilityId={creditFacilityId}
          chartTitle="Excess Concentration Amounts"
          concentrationLimitFormatMap={concentrationLimitFormatMap}
          creditAgreementConcentrationLimits={
            creditAgreementConcentrationLimits
          }
        />
      )}
      {(displayMode === 'all' || displayMode === 'concentration') &&
        concentrationLimitFormatMap.size > 0 && (
          <Flex direction="column" gap="1rem">
            <Typography variant="section-title" trim>
              Concentration Limits
            </Typography>

            {Array.from(concentrationLimitFormatMap).map(
              ([concentrationLimitKey, valueType]) => {
                const creditAgreementConcentrationLimit = creditAgreementConcentrationLimits.find(
                  (concentrationLimit) =>
                    concentrationLimit._config_.concentrationLimitConfig?.propertyKey.toLowerCase() ===
                    concentrationLimitKey.toLowerCase(),
                );

                const chartTitle =
                  creditAgreementConcentrationLimit?.name ??
                  commonUtils.toTitleCase(concentrationLimitKey);

                return (
                  <PortfolioConcentrationLimitsChartTemplate
                    creditFacilityId={creditFacilityId}
                    chartTitle={chartTitle}
                    chartConfig={generateConcentrationLimitChartConfig(
                      valueType === 'number' ? 'float' : valueType,
                    )}
                    concentrationLimitKey={concentrationLimitKey}
                    key={concentrationLimitKey}
                  />
                );
              },
            )}
          </Flex>
        )}
    </Flex>
  );
};
